/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */
import {Dispatch} from 'redux';

import {IActiveUsersData, fetchActiveUsers, filterActiveUserData} from 'services/analytics.service';
import websocket from 'services/websocket.connections';
import {getAdjustedTime} from 'utility/date';
import {IPortalError, transformToPortalError} from 'utility/error-handler';

import {IActiveUserData} from 'views/analytics/current-activity';

/**
 * ACTION TYPES
 */

export const CURRENT_ACTIVITY_FETCHING = 'current-activity::CURRENT_ACTIVITY_FETCHING' as const;
export const CURRENT_ACTIVITY_FETCHING_DONE = 'current-activity::CURRENT_ACTIVITY_FETCHING_DONE' as const;
export const CURRENT_ACTIVITY_FETCHING_ERROR = 'current-activity::CURRENT_ACTIVITY_FETCHING_ERROR' as const;

/**
 * INTERFACES
 */

interface IRequestCurrentActivity {
  type: typeof CURRENT_ACTIVITY_FETCHING;
}
interface IReceiveCurrentActivity {
  type: typeof CURRENT_ACTIVITY_FETCHING_DONE;
  payload: {data: IActiveUserData[]};
}
interface IFailedCurrentActivity {
  type: typeof CURRENT_ACTIVITY_FETCHING_ERROR;
  error: null | IPortalError;
}

export type CurrentActivityActionType = IRequestCurrentActivity | IReceiveCurrentActivity | IFailedCurrentActivity;

interface ICurrentActivityActions {
  request: () => CurrentActivityActionType;
  receive: (payload) => CurrentActivityActionType;
  failed: (error: null | IPortalError) => CurrentActivityActionType;
}

/**
 * ACTIONS
 */

const currentActivityActions: ICurrentActivityActions = {
  request: () => ({type: CURRENT_ACTIVITY_FETCHING}),
  receive: payload => ({
    type: CURRENT_ACTIVITY_FETCHING_DONE,
    payload
  }),
  failed: error => ({
    type: CURRENT_ACTIVITY_FETCHING_ERROR,
    error
  })
};

export const getCurrentActivity = (applicationId: string) => async(dispatch: Dispatch<CurrentActivityActionType>): Promise<IActiveUsersData> => {
  const {
    request,
    receive,
    failed
  } = currentActivityActions;

  dispatch(request());

  try {
    const socket = websocket.getActiveSocket();
    const currentActivity = await fetchActiveUsers(socket, getAdjustedTime().valueOf(), applicationId);

    dispatch(receive({data: filterActiveUserData(currentActivity)}));

    return currentActivity;
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};